import React from "react";
import {
  Button,
  Card,
  Box,
  FormControl,
  TextField,
  Typography,
  Grid,
  FormGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
  IconButton,
  Alert,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { API, Storage } from "aws-amplify";
import { createCustomerMessage } from "../graphql/mutations";
import {
  validateEmail,
  validatePhone,
  validateName,
  validateMessage,
} from "../services/FormValidation";
import { SERVICE_OPTIONS } from "../services/ServiceOptions";
import SEO from "../components/SEO";
import Icon from "@mdi/react";
import { mdiFacebookMessenger as MessengerIcon } from "@mdi/js";

const MAX_FILES = 4;
const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = "image/*,application/pdf,.doc,.docx";

function sanitizeFileName(fileName) {
  return fileName.replace(/[^a-zA-Z0-9._-]+/g, "-");
}

function uniqueUploadKey(fileName) {
  const suffix = Math.random().toString(36).slice(2, 8);
  return `contact-uploads/${Date.now()}-${suffix}-${sanitizeFileName(fileName)}`;
}

function ContactPage({ closeContactForm, openSuccessMessage, preselectedService }) {
  const location = useLocation();
  const navigate = useNavigate();
  const initialService =
    preselectedService || location?.state?.preselectedService || null;

  // Modal usage (desktop) supplies closeContactForm; the standalone /contact route (mobile) does not, so go back instead.
  const dismissContactForm = () => {
    if (typeof closeContactForm === "function") {
      closeContactForm();
    } else {
      navigate(-1);
    }
  };

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [selectedServices, setSelectedServices] = useState(
    initialService && SERVICE_OPTIONS.includes(initialService)
      ? [initialService]
      : []
  );
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [messageError, setMessageError] = useState(false);

  const toggleService = (service) => {
    setSelectedServices((prev) =>
      prev.includes(service)
        ? prev.filter((item) => item !== service)
        : [...prev, service]
    );
  };

  const addFiles = (incomingFiles) => {
    const incomingList = Array.from(incomingFiles || []);
    if (incomingList.length === 0) return;

    setFiles((prev) => {
      const combined = [...prev];
      for (const file of incomingList) {
        if (combined.length >= MAX_FILES) {
          setFileError(`You can attach up to ${MAX_FILES} files.`);
          break;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
          setFileError(`"${file.name}" is larger than 8MB and was not added.`);
          continue;
        }
        const alreadyAdded = combined.some(
          (existing) => existing.name === file.name && existing.size === file.size
        );
        if (!alreadyAdded) {
          combined.push(file);
          setFileError("");
        }
      }
      return combined;
    });
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError("");
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const uploadFiles = async () => {
    const keys = [];
    for (const file of files) {
      const key = uniqueUploadKey(file.name);
      await Storage.put(key, file, {
        level: "public",
        contentType: file.type || "application/octet-stream",
      });
      keys.push(key);
    }
    return keys;
  };

  const submitContactForm = async () => {
    setNameError(validateName(name));
    setEmailError(validateEmail(email));
    if (phone !== "") setPhoneError(validatePhone(phone));
    setMessageError(validateMessage(message));

    if (
      validateName(name) ||
      validateEmail(email) ||
      (phone !== "" && validatePhone(phone)) ||
      validateMessage(message)
    ) {
      return;
    }

    setUploading(true);
    try {
      const attachmentKeys = files.length > 0 ? await uploadFiles() : [];

      const customerMessage = {
        name: name,
        email: email,
        phone: phone ? phone : "(No phone provided)",
        message: message,
        services: selectedServices,
        attachmentKeys: attachmentKeys,
      };

      await API.graphql({
        query: createCustomerMessage,
        variables: {
          input: customerMessage,
        },
      });

      // Google Ads conversion event: Submit lead form
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-18495966123/3QwHCLPLl5IdEKuXyPNE",
        });
      }

      setName("");
      setEmail("");
      setPhone("");
      setMessage("");
      setSelectedServices([]);
      setFiles([]);
      openSuccessMessage(true);
      dismissContactForm();
    } catch (e) {
      console.log(e);
      setFileError("Something went wrong sending your message. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const openMessenger = () => {
    window.open("http://m.me/haasfab", "_blank");
  };

  return (
    <div className="container" style={{}}>
      <SEO
        title="Contact Us"
        description="Request a quote from HaasFab for powder coating, welding, fabrication, CNC plasma cutting, and more. Attach photos and get a fast response."
        path="/contact"
      />
      <Grid
        container
        spacing={0}
        direction="column"
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={3}>
          <Card sx={{ padding: 2 }}>
            <FormControl>
              <Box
                sx={{
                  flexDirection: "column",
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6">Contact Us</Typography>
                <Grid container
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    marginTop: 1,
                  }}
                >
                  <Typography>Complete this form or message us on</Typography>
                  <Button
                    onClick={openMessenger}
                    sx={{ textTransform: "none" }}
                    endIcon={
                      <Icon path={MessengerIcon} size={1} color="#006AFF" />
                    }
                  >
                    <Typography>Facebook</Typography>
                  </Button>
                </Grid>
              </Box>

              <TextField
                id="name"
                variant="standard"
                label="Full Name"
                type="text"
                size="small"
                value={name}
                error={nameError}
                helperText={nameError ? "Enter a valid name" : null}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError(false);
                }}
                sx={{ margin: 1 }}
                required
              />
              <FormGroup row sx={{ display: "flex" }}>
                <TextField
                  id="email"
                  variant="standard"
                  label="Email"
                  type="email"
                  size="small"
                  value={email}
                  error={emailError}
                  helperText={emailError ? "Enter a valid email" : null}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setEmailError(false);
                  }}
                  sx={{ margin: 1 }}
                  required
                />

                <TextField
                  id="phone"
                  variant="standard"
                  label="Phone Number"
                  type="number"
                  size="small"
                  value={phone}
                  error={phoneError}
                  helperText={phoneError ? "Enter a valid phone number" : null}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setPhoneError(false);
                  }}
                  sx={{ margin: 1 }}
                />
              </FormGroup>

              <FormGroup sx={{ display: "flex", flexDirection: "column" }}>
                <TextField
                  id="Message"
                  variant="standard"
                  label="Message"
                  type="text"
                  multiline
                  value={message}
                  error={messageError}
                  helperText={
                    messageError
                      ? `Message must be longer than ${message.length} characters`
                      : null
                  }
                  onChange={(e) => {
                    setMessage(e.target.value);
                    setMessageError(false);
                  }}
                  sx={{ margin: 1 }}
                  inputProps={{ maxLength: 500 }}
                  required
                />
              </FormGroup>

              <FormGroup sx={{ margin: 1 }}>
                <FormLabel>What services are you interested in?</FormLabel>
                <Grid container sx={{ maxWidth: 480 }}>
                  {SERVICE_OPTIONS.map((service) => (
                    <Grid item xs={6} key={service}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={selectedServices.includes(service)}
                            onChange={() => toggleService(service)}
                          />
                        }
                        label={service}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>

              <FormGroup sx={{ margin: 1 }}>
                <FormLabel>Attach photos or files (optional)</FormLabel>
                <Box
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragActive(true);
                  }}
                  onDragLeave={() => setIsDragActive(false)}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById("contact-file-input").click()}
                  sx={{
                    border: "2px dashed",
                    borderColor: isDragActive ? "secondary.main" : "grey.500",
                    borderRadius: 1,
                    padding: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    marginTop: 1,
                  }}
                >
                  <CloudUploadIcon fontSize="large" />
                  <Typography variant="body2">
                    Drag and drop files here, or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Up to {MAX_FILES} files, 8MB each
                  </Typography>
                  <input
                    id="contact-file-input"
                    type="file"
                    multiple
                    accept={ACCEPTED_FILE_TYPES}
                    hidden
                    onChange={(e) => {
                      addFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </Box>

                {fileError && (
                  <Alert severity="warning" sx={{ marginTop: 1 }}>
                    {fileError}
                  </Alert>
                )}

                {files.length > 0 && (
                  <Box sx={{ marginTop: 1 }}>
                    {files.map((file, index) => (
                      <Box
                        key={`${file.name}-${file.size}`}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="body2" noWrap sx={{ maxWidth: 260 }}>
                          {file.name}
                        </Typography>
                        <IconButton size="small" onClick={() => removeFile(index)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </FormGroup>

              <Button
                variant="contained"
                onClick={submitContactForm}
                disabled={uploading}
              >
                {uploading ? "Sending..." : "Submit"}
              </Button>
            </FormControl>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
}

export default ContactPage;
